import { useEffect, useRef, useState } from "react";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import { getLocale, shallowEqual, useSelector } from "umi";
import moment from "moment";
import { downLoadPcConsoleV3DayPerformanceTable, getPcConsoleV3DayPerformanceTable } from "@/pages/dashboard/services/overview";
import { LANGUAGE_MAP, PERIODTIMEOBJ } from "@/pages/dashboard/_config/constant";
import { IsSingleDay } from "@/pages/dashboard/utils/utils";
import { PagingQuery } from "tea-component";
import { getTableRequestParams } from "./hooks";
import { SortBy } from "tea-component/lib/table/addons";
import { PerformanceTableReqParams, TLockColumn } from "./types";
import { stringToFunction } from "@/pages/dashboard/utils/stringToJson";
import { getMultiSortParams } from "@/pages/dashboard/components/OverallTableCo/utils";

const useData = (
  filterValue: any,
  filterLoading: boolean,
  selectType: string,
  config: {
    api: any;
    haveLastDate?: boolean;
    getParams?: (params: PerformanceTableReqParams) => any;
    isSingleDate: boolean; // 是否简单日期，默认有日期区间的页面不走补齐逻辑
    defaultSort: SortBy[];
    serviceParams?:{[key:string]: string; };
    lockColumns?: TLockColumn[];
    columns?: unknown[];
  },
  componentMetrics = [],
  otherParams?: any,
  timeZone?: number,
  // getRequestParams: Function,
) => {
  const {
    api: apiObj,
    getParams,
    isSingleDate = true,
    defaultSort = [],
    haveLastDate,
    serviceParams: restApiConfig,
    lockColumns = [],
    columns = [],
  } = config || {};
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // // 排序
  const [sort, setSort] = useState<SortBy[]>(defaultSort);
  // 总条数
  const [total, setTotal] = useState(0);
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const { overLimitBarTriggerKey, handleResponse } = useOverLimitTrigger();

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);

  // const [,forceUpdate] = useReducer(x=>x+1,0)
  const filterValueSort = useRef(filterValue);
  const accumulateOldValueRef = useRef(['date']);

  const gameCode = useSelector((state: any) => state?.dashboard?.currentGame?.game_code, shallowEqual);

  const metricsList = componentMetrics?.map((item: { reqMetricKey: any; }) => item?.reqMetricKey) || [];

  // 获取表格上传参数
  const getRequestParams = (paging: any, sort: any, filterValue: any, otherParams?: any) => {
    // overview页面的日期上传参数逻辑与其他页面不同, 故特殊处理
    // eslint-disable-next-line prefer-const
    let { rangeTime: [startTime, endTime], diff, timeType } = filterValue || {};
    const periodObj = { period: PERIODTIMEOBJ?.[timeType] || 'daily' };

    /**
     * day 少于七天补足七天
     * week 少于六周补足六周
     * month 少于六月补足六月
     */
    if (diff && isSingleDate) {
      switch (timeType) {
        case "Day": {
          startTime = moment(startTime).subtract(7, 'day').format('YYYY-MM-DD');
          break;
        }
        case "Week": {
          startTime = moment(endTime).week(moment(endTime).week() - 5).startOf('isoWeek').format('YYYY-MM-DD');
          break;
        }
        case "Month": {
          startTime = moment(endTime).month(moment(endTime).month() - 5).startOf('month').format('YYYY-MM-DD');
          break;
        }
      }
    }

    return getTableRequestParams({
      gameCode, filterValue,
      accumulated: accumulateOldValueRef?.current,
      sort: sort,
      paging: paging,
      selectType,
      start_date: moment(startTime).format("YYYYMMDD"),
      end_date: moment(endTime).format("YYYYMMDD"),
      metrics: metricsList || [],
      ...otherParams,
      ...periodObj,
      haveLastDate,
      timeZone,
      options: { lockColumns, columns },
      ...restApiConfig
    });
  };

  useEffect(() => {
    filterValueSort.current = { filterValue, paging };
  }, [filterValue, paging, sort]);

  // 点击筛选器的时候
  // 注意：不将 lockColumns、columns 放入依赖，否则会因引用频繁变化导致无限请求
  useEffect(() => {
    if (filterLoading) return;
    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
    setSort(defaultSort);
    const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, defaultSort, filterValue, {
      ...otherParams,
      multi_sort: getMultiSortParams(lockColumns, columns, defaultSort, selectType, true)
    });
    getTableData(params);
    // 和震捷核对selectType可以删掉
  }, [filterValue, filterLoading]);


  const getTableData = (params: any) => {
    setIsError(false);
    setLoading(true);
    const api = `${apiObj?.tableApi}?language=${LANGUAGE_MAP[getLocale()]}`;
    if (typeof getParams === 'function') {
      params = getParams(params);
    }
    if (typeof config?.getParams === 'string') {
      const fn = stringToFunction?.(config?.getParams);
      if (typeof fn === 'function') {
        params = fn?.(params);
      }
    }
    getPcConsoleV3DayPerformanceTable(api, params).then((resp) => {
      setLoading(false);
      setTableData(resp.metrics_list || []);
      setStatisticsData(resp.aggregation || []);
      // 设置总条数
      setTotal(resp.row_count);
      setFrontLogicalFlag(resp?.front_logical_flag);
      handleResponse(resp?.front_logical_flag);
    }).catch(e => {
      setLoading(false);
      setIsError(true);
      console.log("overview 宽表请求错误: ", filterValue.timeType, e);
    });
  };

  return {
    tableData,
    statisticsData,
    paging,
    loading,
    isError,
    sort,
    total,
    frontLogicalFlag,
    overLimitBarTriggerKey,
    filterValueSort,
    accumulateOldValueRef,
    isSingle: IsSingleDay({ ...filterValue, period: PERIODTIMEOBJ[filterValue?.timeType] }),
    setPaging,
    setSort,
    getRequestParams,
    getData: getTableData,
    downLoadData: downLoadPcConsoleV3DayPerformanceTable
  };
};

export default useData;